<template>
  <div class="date-picker">
    <b-input-group ref="inputGroup">
      <b-input
        class="date-picker__input"
        type="text"
        v-mask="inputDateMask"
        v-model="inputValue"
        v-b-tooltip.hover.bottom="finalPlaceholder"
        autocomplete="off"
        ref="input"
        :disabled="disabled"
        :placeholder="finalPlaceholder"
        :state="state"
        :class="{
          'date-picker__input_no-placeholder': !placeholder,
        }"
        @input="onInputChange()"
        @keydown.tab="turnOffPopover"
        @blur.native="onInputBlur($event)"
      >
      </b-input>
      <b-input-group-append is-text>
        <i
          class="far fa-calendar"
          aria-hidden="true"
        />
      </b-input-group-append>
    </b-input-group>
    <b-popover
      triggers="click"
      placement="bottom"
      custom-class="date-picker__popover"
      :show.sync="showPopover"
      :disabled.sync="disabled"
      :boundary="boundary"
      :target="() => $refs.inputGroup"
    >
      <v-date-picker
        class="date-picker__calendar"
        mode="single"
        is-inline
        :value="datePickerValue"
        :attributes="calendarAttributes"
        :min-date="normalizedMinDate"
        :max-date="normalizedMaxDate"
        @input="onDatePickerInput"
      />
    </b-popover>
  </div>
</template>

<script>
/**
 * Este componente utiliza o v-calendar e o wrapper do date-picker para exibir
 *   um calendário para a data ser selecionada a partir dele.
 *
 * Ele aproveita a props value que pode ser atualizada com um evento chamado input
 *  através de uma computed(selectedDate) com get e set.
 *
 * Para usar, basta utilizar v-model no componente
 *
 * <date-picker v-model="variavelEscolhida" />
 *
 * As props estão com nomes relacionados as suas funcionalidades.
 *
 */
/**
 * @event blur-native-proxy Faz o repasse o evento blur nativo do Input
 * @event lazy-blur-native-proxy Faz o repasse o evento blur nativo do Input, mas tenta evitar
 * o blur quando acontece a seleção por meio do datepicker, onde o input perde o focus
 * temporariamente mas voltar a ficar com focus. Este evento tem um atraso.
 */

import { DatePicker } from 'v-calendar';
import {
  formatter as dateTimeFormatter,
  fromFormat,
  getFinalDate,
  getInitialDate,
  getNow,
  getDay,
  getMonth,
  getYear,
  isValid,
  supressOffset,
} from '@/services/dateTime/datetimeService';
import {
  formats,
} from '@/constants/localizer';

export default {
  name: 'DatePicker',
  props: {
    value: {
      type: [Date, String],
      required: false,
    },
    placeholder: {
      type: String,
      required: false,
    },
    state: {
      type: Boolean,
      default: null,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    boundary: {
      type: String,
      default: 'scrollParent',
    },
    minDate: {
      type: String,
      default: getInitialDate(),
    },
    maxDate: {
      type: String,
      default: getFinalDate(),
    },
    timeZone: {
      type: String || null,
      default: null,
    },
  },
  components: {
    'v-date-picker': DatePicker,
  },
  data() {
    return {
      datePickerValue: null,
      inputValue: null,
      showPopover: false,
      typing: false,
      waitingBlur: null,
    };
  },
  computed: {
    normalizedMinDate() {
      // O componente 'v-date-picker' não suporta string.
      return new Date(this.minDate);
    },
    normalizedMaxDate() {
      // O componente 'v-date-picker' não suporta string.
      return new Date(this.maxDate);
    },
    inputDateMask() {
      return this.parseDateMask.replace(/\w/g, '#');
    },
    parseDateMask() {
      return this.$t('date-time.input.date-mask-luxon');
    },
    finalPlaceholder() {
      return this.placeholder || this.$t('date-time.input.date-placeholder');
    },
    readInputValue() {
      if (!this.inputValue) return null;
      const opts = { timeZone: this.timeZone };
      return fromFormat(this.inputValue, this.parseDateMask, opts);
    },
    calendarAttributes() {
      return [
        {
          key: 'today',
          highlight: {
            backgroundColor: 'var(--color-primary)',
            opacity: 0.1,
          },
          contentStyle: {
            fontWeight: 'bold',
          },
          dates: getNow(),
          popover: {
            label: this.$t('date-time.today'),
          },
        },
      ];
    },
  },
  created() {
    this.updateDatePickerValue(this.value);
    this.updateInputTextValue(this.value);
    this.initClickEvent();
  },
  methods: {
    updateDatePickerValue(value) {
      this.datePickerValue = this.convertToJSDate(value);
    },
    updateInputTextValue(value) {
      if (this.typing) return;
      let dateTimeISO = this.convertToISO(value);
      dateTimeISO = supressOffset(dateTimeISO);
      this.inputValue = dateTimeFormatter(
        dateTimeISO,
        this.parseDateMask,
        this.timeZone,
      );
    },
    updateDataValue(value) {
      this.emitInput(this.convertToISO(value));
    },
    initClickEvent() {
      window.onclick = this.handleClosePopoverOnClick;
    },
    handleClosePopoverOnClick(event) {
      if (typeof (event.target.className) !== 'string') return;
      const className = event.target.className || '';
      if (!className.includes('date-picker')) {
        this.$root.$emit('bv::hide::popover');
        this.turnOffPopover();
      }
    },
    onDatePickerInput(value) {
      // 'value' é um JS date time
      if (value) {
        this.updateDatePickerValue(value);
        this.updateInputTextValue(value);
        this.updateDataValue(value);
      }

      this.clearLazyBlur();
      this.$refs.input.blur();

      this.turnOffPopover();

      this.$emit('input-calendar-click');
    },
    turnOffPopover() {
      this.showPopover = false;
    },
    onInputChange() {
      this.typing = true;

      const inputedValueInISODate = this.readInputValue;

      if (isValid(inputedValueInISODate)) {
        this.updateDataValue(inputedValueInISODate);
        this.updateDatePickerValue(inputedValueInISODate);
      } else {
        this.updateDataValue(null);
      }

      setTimeout(() => {
        this.typing = false;
      }, 500);
    },
    onInputBlur(evt) {
      this.$emit('blur-native-proxy', evt);
      this.setLazyBlur(evt);
    },
    setLazyBlur(evt) {
      this.clearLazyBlur();
      this.waitingBlur = setTimeout(() => {
        this.clearLazyBlur();
        this.$emit('lazy-blur-native-proxy', evt);
      }, 500);
    },
    clearLazyBlur() {
      if (this.waitingBlur != null) {
        clearTimeout(this.waitingBlur);
      }
      this.waitingBlur = null;
    },
    emitInput(newValue) {
      const currentValueISO = this.convertToISO(this.value);
      const newValueISO = this.convertToISO(newValue);

      if (newValueISO !== currentValueISO) {
        this.$emit('input', newValueISO);
      }
    },
    convertToISO(dateTime) {
      if (!dateTime) return null;

      if (typeof (dateTime) === 'string') return dateTime;

      const date = String(dateTime.getDate()).padStart(2, '0');
      const month = String(dateTime.getMonth() + 1).padStart(2, '0');
      const year = dateTime.getFullYear();

      const ISODate = `${year}-${month}-${date}`;
      const opts = { timeZone: this.timeZone };
      const dateTimeISO = fromFormat(ISODate, formats.ISO_DATE, opts);

      return dateTimeISO;
    },
    convertToJSDate(dateTime) {
      if (!dateTime) return null;

      if (typeof (dateTime) === 'object') return dateTime;

      const opts = { timeZone: this.timeZone };
      const day = getDay(dateTime, opts);
      // No 'Date' do JS o valor do mês é representado entre os intervalos 0-11
      const month = getMonth(dateTime, opts) - 1;
      const year = getYear(dateTime, opts);

      return new Date(year, month, day);
    },
  },
  watch: {
    value() {
      this.updateDatePickerValue(this.value);
      this.updateInputTextValue(this.value);
    },
  },
};
</script>

<style src="v-calendar/lib/v-calendar.min.css"></style>
<style lang="sass" scoped>
.date-picker
  &__input
    &_no-placeholder::placeholder
      font-style: initial
  &__popover
    z-index: 10
  ::v-deep &__calendar
    border: none !important
    .popover-content
      background-color: var(--color-background-light) !important
      border-color: var(--color-border-dark) !important
</style>
